const calcMax = (values: number[]): number => {
  if (values.length === 0) {
    return NaN;
  }
  if (values.length < 100000) {
    return Math.max(...values);
  }
  // 配列のサイズが大きすぎると、スプレッド構文(...values)での展開時にメモリ不足エラーになることがあるためreduceを使う
  return values.reduce((maximum: number, value: number): number => {
    return Math.max(maximum, value);
  }, -Infinity);
};

const calcMin = (values: number[]): number => {
  if (values.length === 0) {
    return NaN;
  }
  if (values.length < 100000) {
    return Math.min(...values);
  }
  // 配列のサイズが大きすぎると、スプレッド構文(...values)での展開時にメモリ不足エラーになることがあるためreduceを使う
  return values.reduce((minimum: number, value: number): number => {
    return Math.min(minimum, value);
  }, Infinity);
};

const calcMedian = (values: number[]): number => {
  if (values.length === 0) {
    return NaN;
  }

  // 配列を小さい順にソートする
  const sortedValues = values.toSorted(
    (value1: number, value2: number): number => {
      return value1 - value2;
    }
  );

  // 配列の要素数が偶数か奇数かに気をつけながら中央値を求める
  const length = sortedValues.length;
  if (length % 2 === 0) {
    return (sortedValues[length / 2 - 1] + sortedValues[length / 2]) / 2;
  }
  return sortedValues[(length - 1) / 2];
};

const calcMean = (values: number[]): number => {
  if (values.length === 0) {
    return NaN;
  }
  return (
    values.reduce((sum: number, value: number): number => {
      return sum + value;
    }, 0) / values.length
  );
};

const calcBins = (values: number[], k: number, unit: number = 1): number[] => {
  if (values.length === 0 || k <= 0) {
    return [];
  }

  const min = calcMin(values);
  const max = calcMax(values);

  // 境界値の1つの間隔がunit未満にならないように調整する
  if (max - min < (k - 1) * unit) {
    k = Math.max(1, Math.ceil((max - min) / unit));
  }
  return Array.from({ length: k + 1 }, (_, index: number): number => {
    return min + (max - min) * (index / k);
  });
};

const calcPercentile = (values: number[], k: number): number[] => {
  if (values.length === 0 || k <= 0) {
    return [];
  }

  // 配列の要素数がkより小さい場合はkを要素数に合わせる
  if (values.length < k) {
    k = values.length;
  }

  // 配列を小さい順にソートする
  const sortedValues = values.toSorted(
    (value1: number, value2: number): number => {
      return value1 - value2;
    }
  );

  // 0, 1/k, 2/k, ..., 1のパーセンタイルを用意する
  const percentiles = Array.from(
    { length: k + 1 },
    (_, index: number): number => {
      return index / k;
    }
  );

  return percentiles
    .map((percentile: number): number => {
      const lowerIndex = Math.floor(sortedValues.length * percentile);
      const upperIndex = Math.ceil(sortedValues.length * percentile);

      // 最後は最大値を返す
      if (lowerIndex === sortedValues.length) {
        return sortedValues[sortedValues.length - 1];
      }
      // 整数でちょうど分割できる場合はその値を返す
      if (lowerIndex === upperIndex) {
        return sortedValues[lowerIndex];
      }
      // 整数でちょうど分割できない場合は線形補間で値を求める
      return (
        sortedValues[lowerIndex] +
        (sortedValues[upperIndex] - sortedValues[lowerIndex]) *
          (sortedValues.length * percentile - lowerIndex)
      );
    })
    .filter((value: number, index: number, values: number[]): boolean => {
      // 重複する値を削除する
      return index === values.lastIndexOf(value);
    });
};

const bordersToRanges = (borders: number[]): [number, number][] => {
  // 境界値([a, b, c, d])を範囲([[a, b], [b, c], [c, d]])に変換する
  if (borders.length === 0) {
    return [];
  }
  if (borders.length === 1) {
    return [[borders[0], borders[0]]];
  }
  return borders
    .slice(0, -1)
    .map((border: number, index: number): [number, number] => {
      return [border, borders[index + 1]];
    });
};

export {
  calcMax,
  calcMin,
  calcMedian,
  calcMean,
  calcBins,
  calcPercentile,
  bordersToRanges,
};
